import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { LogOut } from "lucide-react";
import renestLogo from "@/assets/renest-logo-transparent.png";
import LanguageSelector from "./LanguageSelector";
import NotificationBell from "@/components/notifications/NotificationBell";

interface HeaderProps {
  showLogoutButton?: boolean;
}

const Header = ({ showLogoutButton = false }: HeaderProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: t('header.logoutSuccess'),
        description: t('header.logoutSuccessDesc')
      });
      
      navigate('/');
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('header.logoutError')
      });
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center">
          <img 
            src={renestLogo} 
            alt="Renest Logo" 
            className="h-12 sm:h-16 w-auto object-contain"
            width="192"
            height="192"
          />
        </Link>
        
        <nav className="flex items-center gap-2 sm:gap-4">
          <LanguageSelector />
          
          {showLogoutButton && <NotificationBell />}
          {showLogoutButton ? (
            <Button variant="ghost" onClick={handleLogout} className="text-xs sm:text-sm px-2 sm:px-4">
              <LogOut className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('header.logout')}</span>
            </Button>
          ) : (
            <>
              <Button variant="ghost" asChild className="text-xs sm:text-sm px-2 sm:px-4">
                <Link to="/auth">
                  <span className="hidden sm:inline">{t('header.login')}</span>
                  <span className="sm:hidden">{t('header.loginShort')}</span>
                </Link>
              </Button>
              <Button asChild className="text-xs sm:text-sm px-2 sm:px-4">
                <Link to="/company-registration">
                  <span className="hidden sm:inline">{t('header.createCompanyAccount')}</span>
                  <span className="sm:hidden">{t('header.createAccountShort')}</span>
                </Link>
              </Button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
